/**
 * Projects as folders on the user's disk (File System Access API).
 *
 *   My Film/
 *     My Film.cutroom        ← the project document (pretty-printed JSON); opens the app when
 *                              double-clicked once the app is installed (see manifest.webmanifest)
 *     .cutroom/backup.json   ← the version found when the project was last opened
 *     Footage/…              ← media, referenced by relative path
 *     Imported/…             ← media copied in from elsewhere
 *     Exports/…              ← rendered videos
 *
 * A project folder is self-contained: move, copy, zip or sync it, then open it again. The project
 * file is found by its extension, so renaming it is fine.
 */
import { createProject, migrateProject, type Project } from '@ve/core';
import { kv } from '../media/idb.ts';
import type { ProjectFolders } from './folders.ts';
import {
  ProjectStoreError,
  type AccessState,
  type LaunchedProject,
  type NewProject,
  type PickedLocation,
  type ProjectStore,
  type RecentProject,
} from './types.ts';

export const PROJECT_EXTENSION = '.cutroom';
/** The project file name used before projects had their own file type; renamed on open. */
const LEGACY_PROJECT_FILE = 'project.cutroom.json';
const BACKUP_DIR = '.cutroom';
const BACKUP_FILE = 'backup.json';
const RECENTS_KEY = 'projects:recent';
const MAX_RECENTS = 30;

class FolderLocation implements PickedLocation {
  constructor(
    readonly name: string,
    readonly hasProject: boolean,
    readonly handle: FileSystemDirectoryHandle,
  ) {}
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function isNotFound(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'NotFoundError' || err.name === 'TypeMismatchError');
}

/** Characters that are invalid in file and folder names on common file systems. */
function sanitizeName(name: string): string {
  // eslint-disable-next-line no-control-regex -- control characters are exactly what we strip
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').trim() || 'Untitled project';
}

async function exists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name);
    return true;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TypeMismatchError') return true; // a folder of that name
    if (err instanceof DOMException && err.name === 'NotFoundError') {
      try {
        await dir.getDirectoryHandle(name);
        return true;
      } catch {
        return false;
      }
    }
    throw err;
  }
}

function isProjectFileName(name: string): boolean {
  return !name.startsWith('.') && name.toLowerCase().endsWith(PROJECT_EXTENSION);
}

/** Names of the project files directly inside a folder, sorted. */
async function projectFilesIn(folder: FileSystemDirectoryHandle): Promise<string[]> {
  const names: string[] = [];
  for await (const entry of folder.values()) {
    if (entry.kind === 'file' && isProjectFileName(entry.name)) names.push(entry.name);
  }
  return names.sort();
}

/** Whether `file` is the entry of that name directly inside `folder`. */
async function containsEntry(
  folder: FileSystemDirectoryHandle,
  file: FileSystemFileHandle,
): Promise<boolean> {
  try {
    return await (await folder.getFileHandle(file.name)).isSameEntry(file);
  } catch (err) {
    if (isNotFound(err)) return false;
    throw err;
  }
}

async function readText(folder: FileSystemDirectoryHandle, name: string): Promise<string> {
  return (await (await folder.getFileHandle(name)).getFile()).text();
}

async function writeText(dir: FileSystemDirectoryHandle, name: string, text: string): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  // createWritable writes to a swap file that replaces the original only on close: atomic.
  const writable = await handle.createWritable();
  try {
    await writable.write(text);
    await writable.close();
  } catch (err) {
    await writable.abort().catch(() => undefined);
    throw err;
  }
}

export interface FolderProjectStoreDeps {
  folders: ProjectFolders;
  pickDirectory?: (options: DirectoryPickerOptions) => Promise<FileSystemDirectoryHandle>;
  locks?: LockManager;
  recents?: { get(): Promise<RecentProject[] | undefined>; set(value: RecentProject[]): Promise<void> };
}

export class FolderProjectStore implements ProjectStore {
  readonly id = 'local-folder';
  private readonly folders: ProjectFolders;
  private readonly pickDirectory: (options: DirectoryPickerOptions) => Promise<FileSystemDirectoryHandle>;
  private readonly recents: NonNullable<FolderProjectStoreDeps['recents']>;
  private readonly locks: LockManager | undefined;
  /** Project file name per open project (the file is found by extension when unknown). */
  private readonly fileNames = new Map<string, string>();

  constructor(deps: FolderProjectStoreDeps) {
    this.folders = deps.folders;
    this.pickDirectory = deps.pickDirectory ?? ((o) => window.showDirectoryPicker(o));
    this.locks = deps.locks ?? (typeof navigator !== 'undefined' ? navigator.locks : undefined);
    this.recents = deps.recents ?? {
      get: () => kv.get<RecentProject[]>(RECENTS_KEY),
      set: (value) => kv.set(RECENTS_KEY, value),
    };
  }

  isSupported(): boolean {
    return typeof window !== 'undefined' && window.isSecureContext && 'showDirectoryPicker' in window;
  }

  async listRecent(): Promise<RecentProject[]> {
    return [...((await this.recents.get()) ?? [])].sort((a, b) => b.lastOpened - a.lastOpened);
  }

  async forgetRecent(projectId: string): Promise<void> {
    await this.recents.set((await this.listRecent()).filter((r) => r.id !== projectId));
    await this.folders.delete(projectId);
  }

  async access(projectId: string): Promise<AccessState> {
    const folder = await this.folders.get(projectId);
    if (!folder) return 'missing';
    return folder.queryPermission({ mode: 'readwrite' });
  }

  async pickLocation(purpose: 'open' | 'create'): Promise<PickedLocation | null> {
    try {
      const handle = await this.pickDirectory({
        id: purpose === 'open' ? 'cutroom-open' : 'cutroom-create',
        mode: 'readwrite',
        startIn: 'videos',
      });
      const hasProject =
        (await projectFilesIn(handle)).length > 0 || (await exists(handle, LEGACY_PROJECT_FILE));
      return new FolderLocation(handle.name, hasProject, handle);
    } catch (err) {
      if (isAbort(err)) return null;
      throw err;
    }
  }

  async openAt(location: PickedLocation): Promise<Project> {
    const folder = this.handleOf(location);
    return this.load(folder, await this.findProjectFile(folder));
  }

  async createAt(
    location: PickedLocation,
    init: NewProject,
    options: { inPlace?: boolean } = {},
  ): Promise<Project> {
    const parent = this.handleOf(location);
    let folder = parent;
    if (options.inPlace) {
      if (location.hasProject)
        throw new ProjectStoreError(`“${location.name}” already contains a project`, 'invalid');
    } else {
      const base = sanitizeName(init.name);
      let name = base;
      for (let i = 2; await exists(parent, name); i++) name = `${base} (${i})`;
      folder = await parent.getDirectoryHandle(name, { create: true });
    }
    const project = createProject(init.name.trim() || 'Untitled project', init.settings);
    const fileName = `${sanitizeName(project.name)}${PROJECT_EXTENSION}`;
    await writeText(folder, fileName, serialize(project));
    this.fileNames.set(project.id, fileName);
    await this.register(project, folder);
    return project;
  }

  async openRecent(projectId: string): Promise<Project> {
    const folder = await this.folders.get(projectId);
    if (!folder)
      throw new ProjectStoreError('This project’s folder is not known on this device', 'not-found');
    let permission = await folder.queryPermission({ mode: 'readwrite' });
    if (permission === 'prompt') permission = await folder.requestPermission({ mode: 'readwrite' });
    if (permission !== 'granted')
      throw new ProjectStoreError(`Access to “${folder.name}” was not granted`, 'no-access');
    const project = await this.load(folder, await this.findProjectFile(folder, projectId));
    if (project.id !== projectId) {
      throw new ProjectStoreError(`“${folder.name}” now contains a different project`, 'invalid');
    }
    return project;
  }

  async inspectLaunched(file: FileSystemFileHandle): Promise<LaunchedProject> {
    const project = parseProject(await (await file.getFile()).text(), file.name);
    const folder = await this.folders.get(project.id);
    let access: AccessState = 'missing';
    if (folder) {
      const permission = await folder.queryPermission({ mode: 'readwrite' });
      if (permission === 'prompt') access = 'prompt';
      // The known folder may hold a different copy of the project: only use it if the file is there.
      else if (permission === 'granted' && (await containsEntry(folder, file))) access = 'granted';
    }
    return { id: project.id, name: project.name, fileName: file.name, access };
  }

  async openLaunched(file: FileSystemFileHandle): Promise<Project | null> {
    const launched = await this.inspectLaunched(file);
    const known = await this.folders.get(launched.id);
    if (known && launched.access !== 'missing') {
      if (
        launched.access === 'prompt' &&
        (await known.requestPermission({ mode: 'readwrite' })) !== 'granted'
      ) {
        throw new ProjectStoreError(`Access to “${known.name}” was not granted`, 'no-access');
      }
      if (await containsEntry(known, file)) return this.load(known, file.name);
      throw new ProjectStoreError(
        `“${file.name}” is no longer in “${known.name}”. Choose the folder that contains it.`,
        'not-found',
      );
    }
    // Unknown on this device: the browser only hands over the file, but media and exports live in
    // its folder. The picker starts in the file's folder, so confirming is one click.
    let folder: FileSystemDirectoryHandle;
    try {
      folder = await this.pickDirectory({ id: 'cutroom-open', mode: 'readwrite', startIn: file });
    } catch (err) {
      if (isAbort(err)) return null;
      throw err;
    }
    if (!(await containsEntry(folder, file))) {
      throw new ProjectStoreError(
        `“${folder.name}” does not contain “${file.name}”. Choose the folder it is in.`,
        'not-found',
      );
    }
    return this.load(folder, file.name);
  }

  async save(project: Project): Promise<void> {
    const folder = await this.folders.get(project.id);
    if (!folder) throw new ProjectStoreError('The project folder is not connected', 'no-access');
    if ((await folder.queryPermission({ mode: 'readwrite' })) !== 'granted') {
      throw new ProjectStoreError(
        `Access to “${folder.name}” has been lost; reopen the project`,
        'no-access',
      );
    }
    const fileName = this.fileNames.get(project.id) ?? (await this.findProjectFile(folder, project.id));
    await writeText(folder, fileName, serialize(project));
    this.fileNames.set(project.id, fileName);
    await this.touchRecent(project, folder, false);
  }

  async lock(projectId: string): Promise<(() => void) | null> {
    if (!this.locks) return () => undefined; // no Web Locks: allow (best effort)
    return new Promise((resolve) => {
      void this.locks!.request(`cutroom:project:${projectId}`, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(null);
          return undefined;
        }
        // Hold the lock until the returned release function is called.
        return new Promise<void>((release) => resolve(release));
      });
    });
  }

  // -------------------------------------------------------------------------------------------

  private handleOf(location: PickedLocation): FileSystemDirectoryHandle {
    if (!(location instanceof FolderLocation)) throw new Error('Location was not created by this store');
    return location.handle;
  }

  /**
   * The project file in a folder. With several, the one holding `projectId` (several projects in
   * one folder are only supported when opened by their file).
   */
  private async findProjectFile(folder: FileSystemDirectoryHandle, projectId?: string): Promise<string> {
    const names = await projectFilesIn(folder);
    if (names.length === 0) {
      if (await exists(folder, LEGACY_PROJECT_FILE)) return LEGACY_PROJECT_FILE;
      throw new ProjectStoreError(`No project found in “${folder.name}”`, 'not-found');
    }
    if (names.length === 1) return names[0]!;
    if (projectId) {
      for (const name of names) {
        try {
          if ((JSON.parse(await readText(folder, name)) as { id?: unknown }).id === projectId) return name;
        } catch {
          // unreadable: not the one we are looking for
        }
      }
    }
    throw new ProjectStoreError(
      `“${folder.name}” contains several projects (${names.join(', ')}). Open the one you want by its file, or move the others into their own folders.`,
      'invalid',
    );
  }

  private async load(folder: FileSystemDirectoryHandle, fileName: string): Promise<Project> {
    let text: string;
    try {
      text = await readText(folder, fileName);
    } catch (err) {
      if (isNotFound(err)) throw new ProjectStoreError(`No project found in “${folder.name}”`, 'not-found');
      throw err;
    }
    const project = parseProject(text, folder.name);
    if (fileName === LEGACY_PROJECT_FILE) fileName = await this.upgradeLegacyFile(folder, project, text);
    // Keep the version we found, in case a later save goes wrong.
    try {
      const backupDir = await folder.getDirectoryHandle(BACKUP_DIR, { create: true });
      await writeText(backupDir, BACKUP_FILE, text);
    } catch (err) {
      console.warn('Could not write project backup', err);
    }
    this.fileNames.set(project.id, fileName);
    await this.register(project, folder);
    return project;
  }

  /** Renames `project.cutroom.json` to `<name>.cutroom`, so the file opens the app. */
  private async upgradeLegacyFile(
    folder: FileSystemDirectoryHandle,
    project: Project,
    text: string,
  ): Promise<string> {
    const base = sanitizeName(project.name);
    let name = `${base}${PROJECT_EXTENSION}`;
    for (let i = 2; await exists(folder, name); i++) name = `${base} (${i})${PROJECT_EXTENSION}`;
    await writeText(folder, name, text);
    await folder.removeEntry(LEGACY_PROJECT_FILE);
    return name;
  }

  private async register(project: Project, folder: FileSystemDirectoryHandle): Promise<void> {
    await this.folders.set(project.id, folder);
    await this.touchRecent(project, folder, true);
  }

  private async touchRecent(
    project: Project,
    folder: FileSystemDirectoryHandle,
    opened: boolean,
  ): Promise<void> {
    const list = await this.listRecent();
    const previous = list.find((r) => r.id === project.id);
    const entry: RecentProject = {
      id: project.id,
      name: project.name,
      location: folder.name,
      lastOpened: opened || !previous ? Date.now() : previous.lastOpened,
    };
    await this.recents.set([entry, ...list.filter((r) => r.id !== project.id)].slice(0, MAX_RECENTS));
  }
}

function parseProject(text: string, where: string): Project {
  try {
    return migrateProject(JSON.parse(text));
  } catch (err) {
    throw new ProjectStoreError(
      `The project file in “${where}” could not be read: ${err instanceof Error ? err.message : String(err)}`,
      'invalid',
    );
  }
}

function serialize(project: Project): string {
  return `${JSON.stringify(project, null, 2)}\n`;
}
